// P6 sheets (frozen mockups-sal-v1): SCR-SAL-01 sale (lines, then payment), SCR-SAL-03 collection, SCR-SAL-06
// promotion. The server prices every line (preview_sale) so the totals shown are the totals posted.
import { useMemo, useState, type FormEvent } from 'react';
import { useTranslation } from 'react-i18next';
import { keepPreviousData, useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  Badge,
  BottomSheet,
  Button,
  Chips,
  Field,
  FormGrid,
  Input,
  LineAdd,
  List,
  ListRow,
  Notice,
  Select,
  SheetActions,
  StageBar,
  Tabs,
} from '../../design/kit';
import { Refusal } from '../../data/auth';
import type { InventoryRow } from '../../data/stock';
import {
  SALES,
  collectInvoices,
  estimateSale,
  loadUnpaid,
  postSale,
  previewSale,
  savePromotion,
  type Promotion,
  type SaleLineInput,
  type SaleUnit,
} from '../../data/sales';
import { PRE_ORDERS, loadOpenPreOrders } from '../../data/preorders';
import { ROUNDS } from '../../data/rounds';
import { Code, Name } from '../../i18n/Isolate';
import type { Language } from '../../i18n';
import { messageKey } from '../../lib/errors';
import { businessToday, formatDate, formatMoney } from '../../lib/format';
import { MoneyField } from '../cash/CashSheets';
import { parseAmount, useCatalogData, useCustomerGroups, useCustomers } from '../catalog/common';
import { useBundleAvailability } from '../catalog/AdvancedSheets';
import { countWord } from '../stock/StockSheets';

type Problem = { field: string; text: string };

function money2(text: string, allowZero = false): number | null {
  if (!text.trim()) return allowZero ? 0 : null;
  const v = parseAmount(text);
  return v !== null && (allowZero ? v >= 0 : v > 0) && Math.round(v * 100) === v * 100 ? v : null;
}

export function useSaleWords() {
  const { t, i18n } = useTranslation();
  const lang = i18n.language as Language;
  const fmt = (v: number | string) => formatMoney(v, lang);
  const refusal = (err: unknown): string => {
    if (!(err instanceof Refusal)) return t(messageKey('SERVER_ERROR'));
    const d = err.detail;
    if (err.code === 'NOT_ENOUGH_STOCK') {
      const q = `${d.capacity !== null && d.capacity !== undefined ? `${countWord(t, 'box', Number(d.boxes ?? 0))} + ` : ''}${countWord(t, 'piece', Number(d.loose ?? 0))}`;
      return t('err.stockshort', { p: String(d.product ?? ''), q });
    }
    if (err.code === 'OPEN_NOT_CONFIRMED') return t('err.openconfirm');
    return t(messageKey(err.code), { p: String(d.product ?? '') });
  };
  return { fmt, refusal, lang };
}

function useSalesDone(onClose: () => void) {
  const qc = useQueryClient();
  return () => {
    for (const key of [SALES, ROUNDS, ['stock'], ['catalog']]) void qc.invalidateQueries({ queryKey: key });
    onClose();
  };
}

/** SCR-SAL-01: a sale in the Seller's open round — lines, then payment (SAL-01..03, SAL-05, SAL-06, ADR-105). */
export function SaleSheet({
  round,
  seller,
  stock,
  initial,
  onClose,
  onDone,
}: {
  round: string;
  seller: string;
  /** A refused offline sale taken back for editing (ADR-128). */
  initial?: { customer: string; lines: SaleLineInput[] };
  stock: InventoryRow[];
  onClose: () => void;
  onDone: (saleId: string) => void;
}) {
  const { t, i18n } = useTranslation();
  const { fmt, refusal } = useSaleWords();
  const qc = useQueryClient();
  const customers = useCustomers();
  const { products, prices } = useCatalogData();
  const [step, setStep] = useState<0 | 1>(0);
  const [customer, setCustomer] = useState(initial?.customer ?? '');
  const [lines, setLines] = useState<SaleLineInput[]>(initial?.lines ?? []);
  const [product, setProduct] = useState('');
  const [unit, setUnit] = useState<SaleUnit>('piece');
  const [qty, setQty] = useState('');
  const [price, setPrice] = useState('');
  const [disc, setDisc] = useState('');
  const [invDisc, setInvDisc] = useState('');
  const [payment, setPayment] = useState<'full' | 'unpaid' | 'split'>('full');
  const [paidQ, setPaidQ] = useState<Record<number, number>>({});
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [problem, setProblem] = useState<Problem>();
  // ORD-04 / ADR-119: an open pre-order of this customer for this Seller can fill the lines and is delivered by the invoice.
  const [deliver, setDeliver] = useState<string | null>(null);
  const preOrders = useQuery({
    queryKey: [...PRE_ORDERS, 'open', customer, seller],
    queryFn: () => loadOpenPreOrders(customer, seller),
    enabled: Boolean(customer),
  });
  const pre = preOrders.data?.[0];
  const onTruck = stock.filter((r) => r.calc_pieces > 0);
  // PRD-08: bundles sell from their components on this truck (the smallest whole quotient). Truck stock rows carry
  // the truck's id as their location.
  const availability = useBundleAvailability();
  const truckLoc = stock[0]?.location_id;
  const bundles = (products.data ?? [])
    .filter((p) => p.is_bundle && p.active)
    .map((p) => ({
      p,
      n: availability.data?.find((a) => a.bundle_id === p.id && a.truck_id === truckLoc)?.available ?? 0,
    }))
    .filter((b) => b.n > 0);
  const prod = (products.data ?? []).find((p) => p.id === product);
  const catalogue = (id: string, u: SaleUnit) => {
    const pr = prices.data?.get(id);
    return u === 'box' ? (pr?.box_price ?? null) : (pr?.piece_price ?? null);
  };
  const units: SaleUnit[] = prod
    ? [...(prod.sell_piece ? ['piece' as const] : []), ...(prod.sell_box ? ['box' as const] : [])]
    : [];
  const inv = money2(invDisc, true) ?? 0;
  const preview = useQuery({
    queryKey: [...SALES, 'preview', round, customer, lines, inv],
    queryFn: () =>
      previewSale(round, customer, lines, inv, () =>
        estimateSale(
          lines,
          inv,
          (id) => (products.data ?? []).find((p) => p.id === id)?.box_capacity ?? null,
          catalogue,
          { unpaid: cust?.unpaid_total ?? 0, limit: cust?.credit_limit ?? 0 },
        ),
      ),
    enabled: Boolean(customer) && lines.length > 0,
    placeholderData: keepPreviousData,
    retry: false,
    // Without signal the query still runs: it answers with the estimate priced on the phone (ADR-128).
    networkMode: 'always',
  });
  const pv = preview.data && lines.length > 0 ? preview.data : null;
  const previewError = preview.isError ? refusal(preview.error) : undefined;
  const paidFor = (i: number) =>
    payment === 'full' ? (lines[i]?.quantity ?? 0) : payment === 'unpaid' ? 0 : (paidQ[i] ?? 0);
  const paidTotal = pv
    ? pv.lines.reduce((s, l, i) => s + Math.round((l.net * paidFor(i) * 100) / l.quantity) / 100, 0)
    : 0;
  const unpaidTotal = pv ? Math.round((pv.total - paidTotal) * 100) / 100 : 0;
  const creditWarn = pv && pv.credit_limit > 0 && unpaidTotal > 0 && pv.unpaid_now + unpaidTotal > pv.credit_limit;

  const pick = (id: string) => {
    setProduct(id);
    const p = (products.data ?? []).find((x) => x.id === id);
    const u: SaleUnit = p?.sell_piece ? 'piece' : 'box';
    setUnit(u);
    const c = catalogue(id, u);
    setPrice(c !== null ? String(c) : '');
  };
  const addLine = () => {
    const q = /^\d+$/.test(qty.trim()) ? Number(qty) : 0;
    const p = money2(price);
    const d = money2(disc, true);
    if (!prod) return setProblem({ field: 'line', text: t('sale.pick') });
    if (q <= 0) return setProblem({ field: 'line', text: t('err.qty') });
    if (p === null) return setProblem({ field: 'line', text: t('err.unitprice') });
    if (d === null) return setProblem({ field: 'line', text: t('err.invdisc') });
    setProblem(undefined);
    const same = lines.findIndex((l) => l.product === prod.id && l.unit === unit && l.price === p);
    if (same >= 0) {
      setLines(
        lines.map((l, i) => (i === same ? { ...l, quantity: l.quantity + q, line_discount: l.line_discount + d } : l)),
      );
    } else {
      setLines([...lines, { product: prod.id, unit, quantity: q, price: p, line_discount: d, remove_promo: false }]);
    }
    setProduct('');
    setQty('');
    setPrice('');
    setDisc('');
  };
  const save = useMutation({
    // Offline the mutation still runs: it queues the sale on the phone (ADR-128).
    networkMode: 'always',
    mutationFn: () =>
      postSale(
        round,
        customer,
        lines,
        inv,
        payment,
        payment === 'split' ? lines.map((_, i) => ({ line: i, quantity: paidQ[i] ?? 0 })) : [],
        confirmOpen,
        deliver,
        { customer: cust?.name ?? '', total: pv?.total ?? 0 },
      ),
    onSuccess: (r) => {
      for (const key of [SALES, ROUNDS, PRE_ORDERS, ['stock'], ['catalog']])
        void qc.invalidateQueries({ queryKey: key });
      const first = r.sales[0];
      if (first) onDone(first.id);
    },
    onError: (e) => setProblem({ field: 'form', text: refusal(e) }),
  });
  const next = () => {
    if (!customer) return setProblem({ field: 'customer', text: t('err.nocustomer') });
    if (lines.length === 0) return setProblem({ field: 'line', text: t('err.nolines') });
    if (previewError) return setProblem({ field: 'form', text: previewError });
    if (pv && pv.open_boxes > 0 && !confirmOpen) return setProblem({ field: 'open', text: t('err.openconfirm') });
    setProblem(undefined);
    setStep(1);
  };
  const submit = (e: FormEvent) => {
    e.preventDefault();
    if (payment === 'split' && (paidTotal <= 0 || unpaidTotal <= 0))
      return setProblem({ field: 'form', text: t('err.split') });
    save.mutate();
  };
  const name = (id: string) => (products.data ?? []).find((p) => p.id === id)?.name ?? '';
  const fillFromPre = () => {
    if (!pre) return;
    setLines(
      pre.lines
        .filter((l) => catalogue(l.product_id, l.unit) !== null)
        .map((l) => ({
          product: l.product_id,
          unit: l.unit,
          quantity: l.quantity,
          price: catalogue(l.product_id, l.unit) ?? 0,
          line_discount: 0,
          remove_promo: false,
        })),
    );
    setDeliver(pre.id);
    setProblem(undefined);
  };
  const cust = (customers.data ?? []).find((c) => c.id === customer);

  return (
    <BottomSheet title={t('act.newsale')} open onClose={onClose} busy={save.isPending}>
      <form onSubmit={submit} noValidate className="yv-skeleton-stack">
        <StageBar label={t('act.newsale')} steps={[t('sl.step.lines'), t('sl.step.pay')]} current={step} />
        {problem?.field === 'form' && <Notice tone="danger">{problem.text}</Notice>}
        {step === 0 ? (
          <>
            <Field
              label={t('customer')}
              hint={cust ? t('sl.unpaidnow', { a: fmt(cust.unpaid_total), l: fmt(cust.credit_limit) }) : undefined}
              error={problem?.field === 'customer' ? problem.text : undefined}
            >
              {({ id, describedBy, invalid }) => (
                <Select
                  id={id}
                  value={customer}
                  onChange={(e) => {
                    setCustomer(e.target.value);
                    setDeliver(null);
                  }}
                  aria-describedby={describedBy}
                  aria-invalid={invalid || undefined}
                >
                  <option value="">{t('choose')}</option>
                  {(customers.data ?? [])
                    .filter((c) => c.active && !c.anonymized)
                    .map((c) => (
                      <option key={c.id} value={c.id}>
                        {c.name}
                        {c.area ? ` · ${c.area}` : ''}
                      </option>
                    ))}
                </Select>
              )}
            </Field>
            {pre && (
              <>
                <Notice tone="info">
                  {/* The code and the date are isolated so the Arabic line keeps its order (ADR-082). */}
                  {t('ol.found', { n: '[[n]]', q: countWord(t, 'piece', pre.pieces), d: '[[d]]' })
                    .split(/(\[\[[nd]\]\])/)
                    .map((part, i) =>
                      part === '[[n]]' ? (
                        <Code key={i}>{pre.number}</Code>
                      ) : part === '[[d]]' ? (
                        <bdi key={i} className="yv-nowrap">
                          {formatDate(pre.wanted_date, i18n.language as Language)}
                        </bdi>
                      ) : (
                        part
                      ),
                    )}
                </Notice>
                {deliver === pre.id || lines.length > 0 ? (
                  <Chips
                    label={t('ol.deliver', { n: pre.number })}
                    items={[{ id: pre.id, label: t('ol.deliver', { n: pre.number }) }]}
                    selected={deliver === pre.id ? [pre.id] : []}
                    onToggle={() => setDeliver(deliver === pre.id ? null : pre.id)}
                  />
                ) : (
                  <Button icon="box" onClick={fillFromPre}>
                    {t('act.fillpre')}
                  </Button>
                )}
              </>
            )}
            <LineAdd>
              <Field label={t('product')}>
                {({ id }) => (
                  <Select id={id} value={product} onChange={(e) => pick(e.target.value)}>
                    <option value="">{t('choose')}</option>
                    {onTruck.map((r) => (
                      <option key={r.product_id} value={r.product_id}>
                        {r.name} — {r.box_capacity !== null ? `${countWord(t, 'box', r.boxes)} + ` : ''}
                        {countWord(t, 'piece', r.loose)}
                      </option>
                    ))}
                    {bundles.map(({ p, n }) => (
                      <option key={p.id} value={p.id}>
                        {p.name} — {t('bun.availn', { n })}
                      </option>
                    ))}
                  </Select>
                )}
              </Field>
              {units.length > 1 && (
                <Tabs
                  label={t('unit')}
                  value={unit}
                  onChange={(v) => {
                    setUnit(v as SaleUnit);
                    const c = catalogue(product, v as SaleUnit);
                    setPrice(c !== null ? String(c) : '');
                  }}
                  items={units.map((u) => ({ id: u, label: t(`unit.${u}`) }))}
                />
              )}
              <FormGrid>
                <Field label={t('quantity')}>
                  {({ id }) => (
                    <Input id={id} numeric inputMode="numeric" value={qty} onChange={(e) => setQty(e.target.value)} />
                  )}
                </Field>
                <MoneyField
                  label={t('sl.price')}
                  value={price}
                  onChange={setPrice}
                  hint={
                    prod && catalogue(prod.id, unit) !== null
                      ? t('sl.catalogue', { p: fmt(catalogue(prod.id, unit) ?? 0) })
                      : undefined
                  }
                />
              </FormGrid>
              <MoneyField label={t('sl.linedisc')} value={disc} onChange={setDisc} />
              {problem?.field === 'line' && <Notice tone="danger">{problem.text}</Notice>}
              <Button icon="plus" onClick={addLine}>
                {t('line.add')}
              </Button>
            </LineAdd>
            {lines.length > 0 && (
              <List label={t('sl.step.lines')} animateNew>
                {lines.map((l, i) => {
                  const pl = pv?.lines[i];
                  return (
                    <ListRow
                      key={`${l.product}-${l.unit}-${l.price}`}
                      testId={`sline-${i}`}
                      title={<Name>{name(l.product)}</Name>}
                      sub={
                        <>
                          {countWord(t, l.unit, l.quantity)} × {fmt(l.price)}
                          <BundleParts product={l.product} quantity={l.quantity} />
                          {pl?.price_changed && (
                            <>
                              {' '}
                              <s className="muted">{t('sl.catalogue', { p: fmt(pl.catalogue_price) })}</s>
                            </>
                          )}
                          {pl?.promotion && (
                            <>
                              <br />
                              <Badge tone={pl.promo_applied ? 'info' : 'neutral'}>
                                {pl.promo_applied
                                  ? t('sl.promo', { p: pl.promo_pct ?? 0, n: pl.promotion })
                                  : t('sl.promo.removed')}
                              </Badge>
                            </>
                          )}
                          {l.line_discount > 0 && ` · ${t('sl.linedisc')} ${fmt(l.line_discount)}`}
                        </>
                      }
                      value={pl ? fmt(pl.net) : undefined}
                      badge={pl?.price_changed ? <Badge tone="warning">{t('sl.changed')}</Badge> : undefined}
                      actions={
                        <>
                          {pl?.promotion && (
                            <Button
                              pill
                              onClick={() =>
                                setLines(lines.map((x, j) => (j === i ? { ...x, remove_promo: !x.remove_promo } : x)))
                              }
                            >
                              {l.remove_promo ? t('act.resume') : t('sl.promo.remove')}
                            </Button>
                          )}
                          <Button
                            pill
                            icon="close"
                            aria-label={t('remove')}
                            onClick={() => setLines(lines.filter((_, j) => j !== i))}
                          />
                        </>
                      }
                    />
                  );
                })}
              </List>
            )}
            <MoneyField label={t('sl.invdisc')} value={invDisc} onChange={setInvDisc} />
            {previewError && <Notice tone="danger">{previewError}</Notice>}
            {pv?.estimated && <Notice tone="info">{t('off.estimate')}</Notice>}
            {pv && (
              <List label={t('sl.total')}>
                <ListRow title={t('sl.gross')} value={fmt(pv.gross)} />
                <ListRow title={t('sl.promos')} value={fmt(pv.promo_total)} />
                <ListRow title={t('sl.discounts')} value={fmt(pv.discount_total)} />
                <ListRow title={<b>{t('sl.total')}</b>} value={<b data-testid="sale-total">{fmt(pv.total)}</b>} />
              </List>
            )}
            {pv && pv.open_boxes > 0 && (
              <>
                <Notice tone="info">{t('sl.open', { n: pv.open_boxes })}</Notice>
                <Chips
                  label={t('sl.openok')}
                  items={[{ id: 'ok', label: t('sl.openok') }]}
                  selected={confirmOpen ? ['ok'] : []}
                  onToggle={() => setConfirmOpen(!confirmOpen)}
                />
                {problem?.field === 'open' && <Notice tone="danger">{problem.text}</Notice>}
              </>
            )}
            <SheetActions>
              <Button variant="main" onClick={next}>
                {t('sl.next')}
              </Button>
              <Button onClick={onClose}>{t('cancel')}</Button>
            </SheetActions>
          </>
        ) : (
          <>
            <Tabs
              label={t('sl.step.pay')}
              value={payment}
              onChange={(v) => setPayment(v as 'full' | 'unpaid' | 'split')}
              items={[
                { id: 'full', label: t('pay.full') },
                { id: 'unpaid', label: t('pay.unpaid') },
                { id: 'split', label: t('pay.split') },
              ]}
            />
            <p className="muted">
              {payment === 'split' ? `${t('pay.splithint')} ` : ''}
              {t('pay.cash')}
            </p>
            {payment === 'split' && (
              <List label={t('pay.split')}>
                {lines.map((l, i) => (
                  <ListRow
                    key={`${l.product}-${i}`}
                    testId={`split-${i}`}
                    title={<Name>{name(l.product)}</Name>}
                    sub={countWord(t, l.unit, l.quantity)}
                    value={pv ? fmt(pv.lines[i]?.net ?? 0) : undefined}
                    actions={
                      <label className="yv-unit-input yv-unit-input--narrow">
                        <Input
                          numeric
                          inputMode="numeric"
                          aria-label={`${t('sl.splitpaid')} · ${name(l.product)}`}
                          value={String(paidQ[i] ?? 0)}
                          onChange={(e) => {
                            const v = /^\d+$/.test(e.target.value) ? Math.min(Number(e.target.value), l.quantity) : 0;
                            setPaidQ({ ...paidQ, [i]: v });
                          }}
                        />
                        <span className="yv-unit-input__unit" aria-hidden="true">
                          {t(l.unit === 'box' ? 'qty.boxes' : 'qty.pieces')}
                        </span>
                      </label>
                    }
                  />
                ))}
              </List>
            )}
            <List label={t('sl.total')}>
              <ListRow
                title={<b>{t('pay.paid', { a: '' }).trim()}</b>}
                value={<b data-testid="paid-total">{fmt(paidTotal)}</b>}
              />
              <ListRow title={t('pay.rest', { a: '' }).trim()} value={fmt(unpaidTotal)} />
            </List>
            {creditWarn && pv && (
              <Notice tone="warning">
                {t('sl.credit', {
                  l: fmt(pv.credit_limit),
                  a: fmt(pv.unpaid_now),
                  b: fmt(pv.unpaid_now + unpaidTotal),
                })}
              </Notice>
            )}
            <SheetActions>
              <Button type="submit" variant="main" busy={save.isPending}>
                {t('sl.save', { a: fmt(pv?.total ?? 0) })}
              </Button>
              <Button onClick={() => setStep(0)}>{t('sl.back')}</Button>
            </SheetActions>
          </>
        )}
      </form>
    </BottomSheet>
  );
}

/** SCR-SAL-03: whole unpaid invoices of one customer made by this Seller, paid in cash (SAL-07). */
export function CollectSheet({
  round,
  seller,
  initialCustomer,
  onClose,
}: {
  round: string;
  seller: string;
  initialCustomer?: string;
  onClose: () => void;
}) {
  const { t } = useTranslation();
  const { fmt, refusal, lang } = useSaleWords();
  const done = useSalesDone(onClose);
  const unpaid = useQuery({ queryKey: [...SALES, 'unpaid', seller], queryFn: () => loadUnpaid(seller) });
  const customers = useMemo(() => {
    const m = new Map<string, string>();
    for (const s of unpaid.data ?? []) m.set(s.customer_id, s.customer);
    return [...m.entries()];
  }, [unpaid.data]);
  const [customer, setCustomer] = useState(initialCustomer ?? '');
  const [picked, setPicked] = useState<string[]>([]);
  const [problem, setProblem] = useState<string>();
  const theirs = (unpaid.data ?? []).filter((s) => s.customer_id === (customer || customers[0]?.[0]));
  const chosen = customer || customers[0]?.[0] || '';
  const total = theirs.filter((s) => picked.includes(s.id)).reduce((a, s) => a + Number(s.total), 0);
  const days = (d: string) => Math.max(0, Math.round((Date.parse(businessToday()) - Date.parse(d)) / 86400000));
  const save = useMutation({
    networkMode: 'always',
    mutationFn: () =>
      collectInvoices(round, chosen, picked, { customer: customers.find(([id]) => id === chosen)?.[1] ?? '', total }),
    onSuccess: done,
    onError: (e) => setProblem(refusal(e)),
  });
  return (
    <BottomSheet title={t('s63.title')} open onClose={onClose} busy={save.isPending}>
      <div className="yv-skeleton-stack">
        <Field label={t('customer')}>
          {({ id }) => (
            <Select
              id={id}
              value={chosen}
              onChange={(e) => {
                setCustomer(e.target.value);
                setPicked([]);
              }}
            >
              {customers.map(([cid, cname]) => (
                <option key={cid} value={cid}>
                  {cname}
                </option>
              ))}
            </Select>
          )}
        </Field>
        <p className="muted">
          {t('col.pick')} · {t('pay.cash')}
        </p>
        {theirs.length === 0 ? (
          <Notice tone="info">{t('empty.unpaid')}</Notice>
        ) : (
          <Chips
            label={t('col.pick')}
            items={theirs.map((s) => ({
              id: s.id,
              label: `${s.number} · ${formatDate(s.business_date, lang)} · ${fmt(s.total)} · ${t('iv.since', { n: days(s.business_date) })}`,
            }))}
            selected={picked}
            onToggle={(id) => setPicked(picked.includes(id) ? picked.filter((x) => x !== id) : [...picked, id])}
          />
        )}
        {problem && <Notice tone="danger">{problem}</Notice>}
        <SheetActions>
          <Button
            variant="main"
            busy={save.isPending}
            onClick={() => (picked.length ? save.mutate() : setProblem(t('col.nothing')))}
          >
            {t('col.save', { a: fmt(total) })}
          </Button>
          <Button onClick={onClose}>{t('cancel')}</Button>
        </SheetActions>
      </div>
    </BottomSheet>
  );
}

/** SCR-SAL-06: create or edit a promotion (owner, PRM-01). */
export function PromotionSheet({ promotion, onClose }: { promotion?: Promotion | undefined; onClose: () => void }) {
  const { t } = useTranslation();
  const qc = useQueryClient();
  const { products } = useCatalogData();
  const groups = useCustomerGroups();
  const [name, setName] = useState(promotion?.name ?? '');
  const [condition, setCondition] = useState<'qty' | 'amount'>(promotion?.condition ?? 'qty');
  const [minimum, setMinimum] = useState(promotion ? String(promotion.minimum) : '');
  const [pct, setPct] = useState(promotion ? String(promotion.pct) : '');
  const [product, setProduct] = useState(promotion?.product_id ?? '');
  const [group, setGroup] = useState(promotion?.group_id ?? '');
  const [priority, setPriority] = useState(promotion ? String(promotion.priority) : '0');
  const [start, setStart] = useState(promotion?.start_date ?? businessToday());
  const [end, setEnd] = useState(promotion?.end_date ?? businessToday());
  const [problem, setProblem] = useState<Problem>();
  const save = useMutation({
    mutationFn: () =>
      savePromotion({
        id: promotion?.id ?? null,
        name: name.trim(),
        condition,
        minimum: parseAmount(minimum) ?? 0,
        pct: parseAmount(pct) ?? 0,
        product: product || null,
        group: group || null,
        priority: Number(priority) || 0,
        start,
        end,
      }),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: SALES });
      onClose();
    },
    onError: (e) =>
      setProblem({ field: 'form', text: e instanceof Refusal ? t(messageKey(e.code)) : t('err.generic') }),
  });
  const submit = (e: FormEvent) => {
    e.preventDefault();
    const p = parseAmount(pct);
    const m = parseAmount(minimum || '0');
    if (!name.trim()) return setProblem({ field: 'name', text: t('err.required') });
    if (m === null || m < 0) return setProblem({ field: 'min', text: t('err.minimum') });
    if (p === null || p <= 0 || p > 50) return setProblem({ field: 'pct', text: t('err.pct') });
    if (end < start) return setProblem({ field: 'end', text: t('err.dates') });
    setProblem(undefined);
    save.mutate();
  };
  const err = (f: string) => (problem?.field === f ? problem.text : undefined);
  return (
    <BottomSheet title={t('act.newpromo')} open onClose={onClose} busy={save.isPending}>
      <form onSubmit={submit} noValidate className="yv-skeleton-stack">
        {problem?.field === 'form' && <Notice tone="danger">{problem.text}</Notice>}
        <Field label={t('pm.name')} error={err('name')}>
          {({ id, describedBy, invalid }) => (
            <Input
              id={id}
              dir="auto"
              value={name}
              onChange={(e) => setName(e.target.value)}
              aria-describedby={describedBy}
              aria-invalid={invalid || undefined}
            />
          )}
        </Field>
        <Tabs
          label={t('pm.cond')}
          value={condition}
          onChange={(v) => setCondition(v as 'qty' | 'amount')}
          items={[
            { id: 'qty', label: t('pm.minqty') },
            { id: 'amount', label: t('pm.minamount') },
          ]}
        />
        <FormGrid>
          <Field label={t('pm.min')} error={err('min')}>
            {({ id, describedBy }) => (
              <Input
                id={id}
                numeric
                inputMode="decimal"
                value={minimum}
                onChange={(e) => setMinimum(e.target.value)}
                aria-describedby={describedBy}
              />
            )}
          </Field>
          <Field label={t('pm.pct')} error={err('pct')}>
            {({ id, describedBy, invalid }) => (
              <Input
                id={id}
                numeric
                inputMode="decimal"
                value={pct}
                onChange={(e) => setPct(e.target.value)}
                aria-describedby={describedBy}
                aria-invalid={invalid || undefined}
              />
            )}
          </Field>
        </FormGrid>
        <Field label={t('pm.product')}>
          {({ id }) => (
            <Select id={id} value={product} onChange={(e) => setProduct(e.target.value)}>
              <option value="">{t('pm.allprod')}</option>
              {(products.data ?? [])
                .filter((p) => p.active)
                .map((p) => (
                  <option key={p.id} value={p.id}>
                    {p.name}
                  </option>
                ))}
            </Select>
          )}
        </Field>
        <Field label={t('pm.group')}>
          {({ id }) => (
            <Select id={id} value={group} onChange={(e) => setGroup(e.target.value)}>
              <option value="">{t('pm.allcust')}</option>
              {(groups.data ?? [])
                .filter((g) => g.active)
                .map((g) => (
                  <option key={g.id} value={g.id}>
                    {g.name}
                  </option>
                ))}
            </Select>
          )}
        </Field>
        <FormGrid>
          <Field label={t('pm.prio')}>
            {({ id }) => (
              <Input
                id={id}
                numeric
                inputMode="numeric"
                value={priority}
                onChange={(e) => setPriority(e.target.value)}
              />
            )}
          </Field>
          <Field label={t('pm.start')}>
            {({ id }) => (
              <Input id={id} type="date" dir="ltr" value={start} onChange={(e) => setStart(e.target.value)} />
            )}
          </Field>
        </FormGrid>
        <Field label={t('pm.end')} error={err('end')}>
          {({ id, describedBy }) => (
            <Input
              id={id}
              type="date"
              dir="ltr"
              value={end}
              onChange={(e) => setEnd(e.target.value)}
              aria-describedby={describedBy}
            />
          )}
        </Field>
        <SheetActions>
          <Button type="submit" variant="main" busy={save.isPending}>
            {t('pm.save')}
          </Button>
          <Button onClick={onClose}>{t('cancel')}</Button>
        </SheetActions>
      </form>
    </BottomSheet>
  );
}

/** ADR-121: under a bundle line, its components for the quantity sold, in small text. */
export function BundleParts({ product, quantity }: { product: string; quantity: number }) {
  const { products } = useCatalogData();
  const p = (products.data ?? []).find((x) => x.id === product);
  if (!p?.is_bundle || !p.components?.length) return null;
  return (
    <>
      <br />
      <span className="muted" data-testid="bundle-parts">
        {p.components.map((c, i) => (
          <span key={c.product_id}>
            {i > 0 && ' · '}
            <Name>{c.name}</Name> ×{c.quantity * quantity}
          </span>
        ))}
      </span>
    </>
  );
}
